import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import { Caption, Text1, Text1Bold } from 'components/typography/Texts';
import { formatMoneyInteger } from 'utils/numberFormatter';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const BarWrapper = styled.div`
  border: solid 1px ${theme.color.gray};
  border-radius: 60px;
  overflow: hidden;
`;

const BarContent = styled.div<{ progress: number }>`
  background-color: ${theme.color.lightGreen};
  width: ${props => (props.progress > 100 ? 100 : props.progress)}%;
  height: 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const BarGoalText = styled(Text1)`
  font-weight: 700;
  margin-right: 6px;
  padding-left: 16px;
`;

const RemainingWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const RemainingText = styled(Caption)`
  color: ${theme.color.lighterGray2};
  margin: 4px 0 0 0;
`;

const CollectedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const CollectedText = styled(Text1Bold)`
  margin: 0;
`;

interface Props {
  value: number;
  total?: number;
}

export const CampaignProgressBar = (props: Props) => {
  const { value, total } = props;
  const { t } = useTranslation();
  const calculateProgress = () => {
    if (!total) return 0;
    const progress = (value * 100) / total;
    if (progress < 0) return 0;
    return progress;
  };

  const progress = calculateProgress();
  const remaining = total ? total - value : 0;

  return (
    <Container>
      {!!total && (
        <>
          <BarWrapper data-testid={'progress-bar'}>
            <BarContent progress={progress}>
              <BarGoalText data-testid={'progress-bar-goal-collected'}>{`${formatMoneyInteger(value)}`}</BarGoalText>
              <Text1 data-testid={'progress-bar-progress'}>{`(${progress.toFixed(2)}%)`}</Text1>
            </BarContent>
          </BarWrapper>
          <RemainingWrapper>
            {remaining > 0 && (
              <RemainingText data-testid={'progress-bar-missing'}>
                {`${t('progress-bar.missing')} ${formatMoneyInteger(remaining)}`}
              </RemainingText>
            )}
          </RemainingWrapper>
        </>
      )}
      {!total && (
        <CollectedWrapper>
          <CollectedText data-testid={'goal-collected'}>{`${formatMoneyInteger(value)}`}</CollectedText>
        </CollectedWrapper>
      )}
    </Container>
  );
};
